interface CloudinaryObject {
  cloud: string;
  transformations?: string;
  version?: string;
  id?: string;
}
type ContentType = "card" | "image" | "banner" | "logo";

interface CloudinaryOptions {
  width?: number;
  height?: number;
  contentType?: ContentType;
}

export enum ImageBreakpoint {
  Logo = 480,
  Mobile = 768,
  Card = 980,
  Small = 1920, // 1080p
  Medium = 2880, // 2k
  Large = 3840, // 4k
}

const getCloudinaryImageBreakpointWidth = (
  windowWidth?: number,
  contentType?: ContentType
): number => {
  if (contentType === "logo") return ImageBreakpoint.Logo;
  if (contentType === "card") return ImageBreakpoint.Card;
  if (windowWidth && windowWidth <= ImageBreakpoint.Small) return ImageBreakpoint.Small;
  if (windowWidth && windowWidth <= ImageBreakpoint.Medium) return ImageBreakpoint.Medium;
  return ImageBreakpoint.Large;
};

const parseCloudinaryURL = (URL: string): CloudinaryObject => {
  // Capture groups: [1] host, [2] cloud name, [3] resource, [4] id
  const cloudinaryURLRegex = /https?:\/\/([^/]+)\/([^/]+)\/[^/]+\/([^/]+)\/(.*)/gm;

  const matches = cloudinaryURLRegex.exec(URL);
  if (!matches) return { cloud: "" };

  const cloudinaryUrlInfos: CloudinaryObject = {
    cloud: matches[2] ?? "john-paul",
  };
  if (!matches[4]) return cloudinaryUrlInfos;

  const transformations: string[] = [];
  const entries = matches[4].split("/");
  let entriesIndex = 0;
  const isTransformationRegex = /(\b[a-z]{1}_\w+)/gm;
  const isVersionNumberRegex = /v[0-9]+/gm;

  while (entriesIndex < entries.length) {
    const transformationMatch = isTransformationRegex.exec(entries[entriesIndex]);
    if (transformationMatch) {
      transformations.push(entries[entriesIndex]);
      entriesIndex++;
      continue;
    }
    const versionMatch = isVersionNumberRegex.exec(entries[entriesIndex]);
    if (versionMatch) {
      cloudinaryUrlInfos.version = entries[entriesIndex];
      entriesIndex++;
      continue;
    }
    cloudinaryUrlInfos.id = entries.splice(entriesIndex, entries.length - entriesIndex).join("/");
    break;
  }
  if (transformations.length > 0) cloudinaryUrlInfos.transformations = transformations.join("/");

  return cloudinaryUrlInfos;
};

export function getCloudinaryOptimizedUrl(url: string, options?: CloudinaryOptions): string {
  const imageInfos = parseCloudinaryURL(url);
  if (imageInfos.cloud === "") return url;

  const breakpointWidth = getCloudinaryImageBreakpointWidth(options?.width, options?.contentType);

  let transformation = imageInfos.transformations ?? "";
  const separator = transformation !== "" ? "/" : "";
  transformation += `${separator}c_limit,w_${breakpointWidth},f_auto,q_auto:eco`;

  // Additional transformation for mobile images to keep good visual quality
  if (options?.height && options?.width && options?.width <= ImageBreakpoint.Mobile) {
    transformation += `/c_fill,h_${options.height},w_${options.width}`;
  }

  return `https://res.cloudinary.com/${imageInfos.cloud}/image/upload/${transformation}/${
    imageInfos.version ? imageInfos.version + (imageInfos.id ? "/" : "") : ""
  }${imageInfos.id ?? ""}`;
}
